import React, { useEffect } from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";

const TermsOfService = () => {
  // Animation on scroll effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate__animated", "animate__fadeInUp");
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll(".animate-on-scroll");
    elements.forEach((el) => observer.observe(el));

    return () => {
      elements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  const handleAgree = () => {
    window.history.back();
  };

  const sections = [
    {
      id: "booking-policy",
      title: "Booking Policy",
      icon: "bi-calendar-check",
      content: (
        <div className="space-y-3">
          <p className="text-secondary">
            All bookings are subject to room availability and confirmation. 
            Once confirmed, you will receive a booking reference via email or SMS.
          </p>
          <ul className="list-unstyled">
            {[
              "Customers are responsible for verifying booking details and ensuring their accuracy",
              "Booking modifications are subject to availability and may incur additional charges",
              "Special requests are accommodated subject to hotel discretion",
              "Group bookings (5+ rooms) require separate terms and conditions"
            ].map((item, idx) => (
              <li key={idx} className="d-flex align-items-start gap-2 mb-2">
                <i className="bi bi-check-circle-fill text-gold mt-1" style={{ color: "#d4af37" }}></i>
                <span className="text-secondary">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )
    },
    {
      id: "payments",
      title: "Payments",
      icon: "bi-credit-card",
      content: (
        <div className="space-y-3">
          <p className="text-secondary">
            Full or partial payment must be made at the time of booking depending on the selected hotel policy. 
            All payments are processed securely via Jayraj Group's payment partners.
          </p>
          <div className="row g-3 mt-2">
            {[
              { icon: "bi-shield-check", title: "Secure Payment", desc: "256-bit SSL encryption for all transactions" },
              { icon: "bi-clock", title: "Payment Timeline", desc: "Full payment required 24-48 hours before check-in" },
              { icon: "bi-wallet", title: "Multiple Methods", desc: "Cards, UPI, Net Banking, and Wallets accepted" }
            ].map((item, idx) => (
              <Col md={4} key={idx}>
                <div className="p-3 rounded-3 text-center" style={{ background: "rgba(212, 175, 55, 0.05)" }}>
                  <i className={`${item.icon} fs-3 mb-2`} style={{ color: "#d4af37" }}></i>
                  <h6 className="fw-semibold text-dark mb-1">{item.title}</h6>
                  <p className="text-secondary small mb-0">{item.desc}</p>
                </div>
              </Col>
            ))}
          </div>
        </div>
      )
    },
    {
      id: "cancellations",
      title: "Cancellations & Refunds",
      icon: "bi-arrow-counterclockwise",
      content: (
        <div className="space-y-3">
          <p className="text-secondary">
            Cancellation requests are subject to the specific hotel's policy. 
            Refunds, if applicable, will be processed within 7–10 business days.
          </p>
          <div className="rounded-3 p-4" style={{ background: "rgba(212, 175, 55, 0.06)", borderLeft: "4px solid #d4af37" }}>
            <h6 className="fw-semibold text-dark mb-2">Cancellation Policy by Timeframe:</h6>
            <ul className="list-unstyled">
              {[
                "24+ hours before check-in: Full refund (minus processing fees)",
                "12-24 hours before check-in: 50% refund",
                "Less than 12 hours before check-in: No refund",
                "Non-refundable bookings: Not eligible for any refund"
              ].map((item, idx) => (
                <li key={idx} className="d-flex align-items-start gap-2 mb-2">
                  <i className="bi bi-clock-history text-gold mt-1" style={{ color: "#d4af37" }}></i>
                  <span className="text-secondary small">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-warning bg-opacity-10 rounded-3 p-3" style={{ border: "1px solid #d4af37" }}>
            <p className="text-dark small mb-0">
              <i className="bi bi-info-circle-fill text-gold me-2" style={{ color: "#d4af37" }}></i>
              <strong>Note:</strong> Refunds are processed to the original payment method within 7-10 business days.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "guest-responsibilities",
      title: "Guest Responsibilities",
      icon: "bi-person-check",
      content: (
        <div className="space-y-3">
          <p className="text-secondary">Guests are expected to adhere to the hotel's rules and regulations:</p>
          <ul className="list-unstyled">
            {[
              "Valid government ID proof required at check-in",
              "Comply with hotel policies including noise restrictions and smoking regulations",
              "Report any damages immediately to hotel staff",
              "Guests are responsible for their personal belongings",
              "Check-in time: 12:00 PM | Check-out time: 10:00 AM"
            ].map((item, idx) => (
              <li key={idx} className="d-flex align-items-start gap-2 mb-2">
                <i className="bi bi-check-circle-fill text-gold mt-1" style={{ color: "#d4af37" }}></i>
                <span className="text-secondary">{item}</span>
              </li>
            ))}
          </ul>
          <div className="rounded-3 p-3" style={{ background: "rgba(220, 53, 69, 0.05)", borderLeft: "4px solid #dc3545" }}>
            <p className="text-danger small mb-0">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              Any damage or misconduct will result in penalties or cancellation of stay without refund.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "liability",
      title: "Liability",
      icon: "bi-shield-exclamation",
      content: (
        <div className="space-y-3">
          <p className="text-secondary">
            Jayraj Group acts as an intermediary between the guest and hotel. 
            We are not liable for loss, damage, or injury caused by hotel management or third-party service providers.
          </p>
          <div className="row g-3 mt-2">
            {[
              { icon: "bi-building", title: "Hotel Responsibility", desc: "Hotels are responsible for service quality, cleanliness, and amenities" },
              { icon: "bi-car-front", title: "Transport Services", desc: "Third-party transport providers are responsible for their services" },
              { icon: "bi-umbrella", title: "Force Majeure", desc: "Not liable for events beyond our control (natural disasters, strikes, etc.)" }
            ].map((item, idx) => (
              <Col md={4} key={idx}>
                <div className="p-3 rounded-3" style={{ background: "rgba(212, 175, 55, 0.05)" }}>
                  <i className={`${item.icon} fs-3 mb-2`} style={{ color: "#d4af37" }}></i>
                  <h6 className="fw-semibold text-dark mb-1">{item.title}</h6>
                  <p className="text-secondary small mb-0">{item.desc}</p>
                </div>
              </Col>
            ))}
          </div>
        </div>
      )
    },
    {
      id: "modifications",
      title: "Modifications",
      icon: "bi-pencil-square",
      content: (
        <div className="space-y-3">
          <p className="text-secondary">
            Jayraj Group reserves the right to modify these Terms at any time. 
            Continued use of our services implies acceptance of the latest version.
          </p>
          <div className="rounded-3 p-4" style={{ background: "rgba(212, 175, 55, 0.06)", border: "1px solid rgba(212, 175, 55, 0.2)" }}>
            <h6 className="fw-semibold text-dark mb-2">What We May Change:</h6>
            <ul className="list-unstyled">
              {[
                "Pricing and payment policies",
                "Cancellation and refund procedures",
                "Service offerings and features",
                "User obligations and responsibilities"
              ].map((item, idx) => (
                <li key={idx} className="d-flex align-items-start gap-2 mb-2">
                  <i className="bi bi-arrow-right-circle-fill text-gold mt-1" style={{ color: "#d4af37" }}></i>
                  <span className="text-secondary">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <p className="text-secondary small">
            <i className="bi bi-bell-fill me-2" style={{ color: "#d4af37" }}></i>
            We will notify you of significant changes via email or website notification.
          </p>
        </div>
      )
    },
    {
      id: "dispute-resolution",
      title: "Dispute Resolution",
      icon: "bi-gavel",
      content: (
        <div className="space-y-3">
          <p className="text-secondary">
            Any disputes arising from these Terms shall be governed by the laws of India and subject to the jurisdiction of courts in Uttarakhand.
          </p>
          <div className="row g-3 mt-2">
            {[
              { icon: "bi-chat", title: "Informal Resolution", desc: "Attempt to resolve disputes amicably within 30 days" },
              { icon: "bi-file-earmark", title: "Written Notice", desc: "Formal complaints must be submitted in writing" },
              { icon: "bi-building", title: "Mediation", desc: "Mediation through a neutral third party if required" }
            ].map((item, idx) => (
              <Col md={4} key={idx}>
                <div className="p-3 rounded-3 text-center" style={{ background: "rgba(212, 175, 55, 0.05)" }}>
                  <i className={`${item.icon} fs-3 mb-2`} style={{ color: "#d4af37" }}></i>
                  <h6 className="fw-semibold text-dark mb-1">{item.title}</h6>
                  <p className="text-secondary small mb-0">{item.desc}</p>
                </div>
              </Col>
            ))}
          </div>
        </div>
      )
    },
    {
      id: "privacy",
      title: "Privacy & Data Protection",
      icon: "bi-lock",
      content: (
        <div className="space-y-3">
          <p className="text-secondary">
            Your privacy is important to us. We collect and process your data in accordance with our Privacy Policy.
          </p>
          <ul className="list-unstyled">
            {[
              "Personal data is collected for booking purposes only",
              "Your data is protected with industry-standard encryption",
              "We do not sell or share your data with third parties for marketing",
              "You can request data deletion at any time"
            ].map((item, idx) => (
              <li key={idx} className="d-flex align-items-start gap-2 mb-2">
                <i className="bi bi-check-circle-fill text-gold mt-1" style={{ color: "#d4af37" }}></i>
                <span className="text-secondary">{item}</span>
              </li>
            ))}
          </ul>
          <div className="rounded-3 p-3" style={{ background: "rgba(212, 175, 55, 0.06)", border: "1px solid rgba(212, 175, 55, 0.2)" }}>
            <p className="text-dark small mb-0">
              <i className="bi bi-envelope-fill text-gold me-2" style={{ color: "#d4af37" }}></i>
              For privacy concerns, contact us at <strong style={{ color: "#d4af37" }}>info@jayrajgroup.org</strong>
            </p>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="terms-section" style={{ 
      background: "linear-gradient(135deg, #faf8f5, #f5f0e8)",
      minHeight: "100vh"
    }}>
      {/* Gold Accent Line */}
      <div style={{ height: "4px", background: "linear-gradient(90deg, #d4af37, #f5d37c, #d4af37)" }}></div>

      {/* Hero Section with Gold Theme */}
      <div className="py-5" style={{ 
        background: "linear-gradient(135deg, #1a1a1a, #2d2d2d)",
        position: "relative",
        overflow: "hidden"
      }}>
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg={8}>
              <div className="mb-4 animate__animated animate__fadeInDown">
                <div className="d-inline-flex align-items-center justify-content-center rounded-circle p-3 mb-3" style={{ 
                  background: "rgba(212, 175, 55, 0.15)",
                  border: "2px solid #d4af37"
                }}>
                  <i className="bi bi-file-text fs-1" style={{ color: "#d4af37" }}></i>
                </div>
                <h1 className="display-4 fw-bold text-white">
                  <span style={{ color: "#d4af37" }}>Terms of</span> Service
                </h1>
                <p className="text-light opacity-75 fs-5 mt-3">
                  Please read these terms carefully before using our travel booking services.
                  By using our platform, you agree to be bound by these terms.
                </p>
                <div className="d-flex justify-content-center gap-3 mt-3 flex-wrap">
                  <span className="badge px-4 py-2" style={{ background: "rgba(212, 175, 55, 0.2)", color: "#d4af37" }}>
                    <i className="bi bi-calendar3 me-2"></i> Last Updated: May 2026
                  </span>
                  <span className="badge px-4 py-2" style={{ background: "rgba(212, 175, 55, 0.2)", color: "#d4af37" }}>
                    <i className="bi bi-clock me-2"></i> Effective Immediately
                  </span>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
        {/* Decorative Elements */}
        <div style={{
          position: "absolute",
          top: "-50%",
          right: "-10%",
          width: "300px",
          height: "300px",
          borderRadius: "50%",
          background: "rgba(212, 175, 55, 0.03)",
          pointerEvents: "none"
        }}></div>
        <div style={{
          position: "absolute",
          bottom: "-30%",
          left: "-5%",
          width: "200px",
          height: "200px",
          borderRadius: "50%",
          background: "rgba(212, 175, 55, 0.02)",
          pointerEvents: "none"
        }}></div>
      </div>

      <Container className="py-5">
        <Row>
          {/* Sidebar Navigation */}
          <Col lg={3} className="mb-4">
            <div className="position-sticky" style={{ top: "2rem" }}>
              <Card className="border-0 shadow-lg rounded-4" style={{ background: "rgba(255, 255, 255, 0.9)", backdropFilter: "blur(8px)" }}>
                <Card.Body className="p-4">
                  <h5 className="fw-bold text-dark mb-3" style={{ borderBottom: "2px solid #d4af37", paddingBottom: "0.75rem" }}>
                    <i className="bi bi-list-ul me-2" style={{ color: "#d4af37" }}></i>
                    Quick Links
                  </h5>
                  <nav className="d-flex flex-column gap-1">
                    {sections.map((section) => (
                      <a
                        key={section.id}
                        href={`#${section.id}`}
                        className="text-decoration-none py-2 px-3 rounded-3 transition-all"
                        style={{ 
                          color: "#4a4a4a",
                          fontSize: "0.9rem",
                          transition: "all 0.3s ease"
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = "rgba(212, 175, 55, 0.1)";
                          e.target.style.color = "#d4af37";
                          e.target.style.paddingLeft = "1.25rem";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = "transparent";
                          e.target.style.color = "#4a4a4a";
                          e.target.style.paddingLeft = "1rem";
                        }}
                      >
                        <i className={`${section.icon} me-2`} style={{ color: "#d4af37", width: "18px" }}></i>
                        {section.title}
                      </a>
                    ))}
                  </nav>
                </Card.Body>
              </Card>
            </div>
          </Col>

          {/* Main Content */}
          <Col lg={9}>
            {/* Introduction */}
            <Card className="border-0 shadow-lg rounded-4 mb-4 animate-on-scroll" style={{ background: "rgba(255, 255, 255, 0.85)", backdropFilter: "blur(8px)" }}>
              <Card.Body className="p-4 p-lg-5">
                <h2 className="fw-bold text-dark mb-3">
                  <span style={{ color: "#d4af37" }}>Introduction</span>
                </h2>
                <div className="text-secondary" style={{ lineHeight: "1.8" }}>
                  <p>
                    Welcome to <strong className="text-dark">Jayraj Group</strong> ("we," "our," or "us"). 
                    These Terms of Service govern your use of our website, mobile application, and services 
                    for booking <strong className="text-dark">tours, buses, flights, and hotels</strong> across India.
                  </p>
                  <p>
                    By accessing or using our platform, you agree to be bound by these terms. 
                    If you do not agree with any part of these terms, please do not use our services.
                  </p>
                  <div className="rounded-3 p-4 mt-3" style={{ background: "rgba(212, 175, 55, 0.08)", borderLeft: "4px solid #d4af37" }}>
                    <p className="text-dark small mb-0">
                      <i className="bi bi-info-circle-fill me-2" style={{ color: "#d4af37" }}></i>
                      <strong>Grievance Officer:</strong> For any queries regarding these Terms, contact us at 
                      <strong className="text-gold" style={{ color: "#d4af37" }}> info@jayrajgroup.org</strong> or call 
                      <strong className="text-gold" style={{ color: "#d4af37" }}> +91-8869872377</strong>
                    </p>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Policy Sections */}
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <Card 
                  key={section.id} 
                  id={section.id} 
                  className="border-0 shadow-lg rounded-4 mb-4 animate-on-scroll"
                  style={{ 
                    background: "rgba(255, 255, 255, 0.85)", 
                    backdropFilter: "blur(8px)",
                    transition: "all 0.3s ease",
                    scrollMarginTop: "2rem"
                  }}
                >
                  <Card.Body className="p-4 p-lg-5">
                    <div className="d-flex align-items-center gap-3 mb-4">
                      <div className="d-flex align-items-center justify-content-center rounded-circle p-3" style={{ 
                        background: "rgba(212, 175, 55, 0.12)",
                        border: "1px solid rgba(212, 175, 55, 0.3)"
                      }}>
                        <i className={`${Icon} fs-4`} style={{ color: "#d4af37" }}></i>
                      </div>
                      <h2 className="fw-bold text-dark mb-0" style={{ fontSize: "1.5rem" }}>
                        {section.title}
                      </h2>
                    </div>
                    <div style={{ color: "#4a4a4a" }}>
                      {section.content}
                    </div>
                  </Card.Body>
                </Card>
              );
            })}

            {/* Acceptance Button */}
            <Card className="border-0 shadow-lg rounded-4 mb-4 animate-on-scroll" style={{ 
              background: "linear-gradient(135deg, rgba(26, 26, 26, 0.95), rgba(45, 45, 45, 0.95))",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(212, 175, 55, 0.3)"
            }}>
              <Card.Body className="p-4 p-lg-5 text-center">
                <h3 className="fw-bold text-white mb-3">
                  <span style={{ color: "#d4af37" }}>Accept</span> Terms
                </h3>
                <p className="text-light opacity-75 mb-4">
                  By clicking "I Agree & Continue", you acknowledge that you have read, understood, 
                  and agree to be bound by these Terms of Service.
                </p>
                <button 
                  onClick={handleAgree}
                  className="btn px-5 py-3 rounded-pill fw-bold"
                  style={{
                    background: "linear-gradient(135deg, #d4af37, #f5d37c)",
                    color: "#1a1a1a",
                    fontSize: "1.1rem",
                    border: "none",
                    boxShadow: "0 4px 20px rgba(212, 175, 55, 0.4)",
                    transition: "all 0.3s ease"
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = "scale(1.05)";
                    e.target.style.boxShadow = "0 8px 30px rgba(212, 175, 55, 0.6)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = "scale(1)";
                    e.target.style.boxShadow = "0 4px 20px rgba(212, 175, 55, 0.4)";
                  }}
                >
                  <i className="bi bi-check-circle-fill me-2"></i>
                  I Agree & Continue
                </button>
                <p className="text-light-50 small mt-3" style={{ color: "rgba(255,255,255,0.4)" }}>
                  By continuing, you agree to our Terms of Service and Privacy Policy
                </p>
              </Card.Body>
            </Card>

            {/* Contact Section */}
            <Card className="border-0 shadow-lg rounded-4 mb-4 animate-on-scroll" style={{ 
              background: "linear-gradient(135deg, rgba(26, 26, 26, 0.95), rgba(45, 45, 45, 0.95))",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(212, 175, 55, 0.3)"
            }}>
              <Card.Body className="p-4 p-lg-5">
                <h2 className="fw-bold text-white mb-4">
                  <span style={{ color: "#d4af37" }}>Contact</span> Us
                </h2>
                <Row className="g-4">
                  <Col md={6}>
                    <div className="d-flex align-items-start gap-3">
                      <div className="d-flex align-items-center justify-content-center rounded-circle p-2" style={{ 
                        background: "rgba(212, 175, 55, 0.15)",
                        minWidth: "40px",
                        minHeight: "40px"
                      }}>
                        <i className="bi bi-envelope-fill" style={{ color: "#d4af37" }}></i>
                      </div>
                      <div>
                        <p className="text-light-50 small mb-1" style={{ color: "rgba(255,255,255,0.6)" }}>Email</p>
                        <p className="text-white mb-0">support@jayrajgroup.org</p>
                        <p className="text-white mb-0">info@jayrajgroup.org</p>
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="d-flex align-items-start gap-3">
                      <div className="d-flex align-items-center justify-content-center rounded-circle p-2" style={{ 
                        background: "rgba(212, 175, 55, 0.15)",
                        minWidth: "40px",
                        minHeight: "40px"
                      }}>
                        <i className="bi bi-telephone-fill" style={{ color: "#d4af37" }}></i>
                      </div>
                      <div>
                        <p className="text-light-50 small mb-1" style={{ color: "rgba(255,255,255,0.6)" }}>Phone</p>
                        <p className="text-white mb-0">+91-8869872377</p>
                        <p className="text-white mb-0">+91-8869872377 (24/7 Support)</p>
                      </div>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="d-flex align-items-start gap-3">
                      <div className="d-flex align-items-center justify-content-center rounded-circle p-2" style={{ 
                        background: "rgba(212, 175, 55, 0.15)",
                        minWidth: "40px",
                        minHeight: "40px"
                      }}>
                        <i className="bi bi-geo-alt-fill" style={{ color: "#d4af37" }}></i>
                      </div>
                      <div>
                        <p className="text-light-50 small mb-1" style={{ color: "rgba(255,255,255,0.6)" }}>Address</p>
                        <p className="text-white mb-0">Jayraj Group, Semitalli, Kedarnath Main Road,</p>
                        <p className="text-white mb-0">Guptkashi, Dist. Rudraprayag,</p>
                        <p className="text-white mb-0">Uttarakhand – 246439</p>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Footer */}
            <div className="text-center py-4">
              <p className="text-secondary small mb-0">
                <i className="bi bi-file-text me-1" style={{ color: "#d4af37" }}></i>
                These Terms of Service are governed by the laws of India.
              </p>
              <p className="text-secondary small mt-2">
                © {new Date().getFullYear()} Jayraj Group. All rights reserved.
              </p>
              <div className="d-flex justify-content-center gap-3 mt-2 flex-wrap">
                <span className="badge" style={{ background: "rgba(212, 175, 55, 0.1)", color: "#d4af37" }}>
                  <i className="bi bi-bus-front me-1"></i> Tours
                </span>
                <span className="badge" style={{ background: "rgba(212, 175, 55, 0.1)", color: "#d4af37" }}>
                  <i className="bi bi-airplane me-1"></i> Flights
                </span>
                <span className="badge" style={{ background: "rgba(212, 175, 55, 0.1)", color: "#d4af37" }}>
                  <i className="bi bi-building-hotel me-1"></i> Hotels
                </span>
                <span className="badge" style={{ background: "rgba(212, 175, 55, 0.1)", color: "#d4af37" }}>
                  <i className="bi bi-geo-alt-fill me-1"></i> Live Tracking
                </span>
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      <style jsx>{`
        .text-gold {
          color: #d4af37;
        }
        .bg-gold {
          background: #d4af37;
        }
        .animate-on-scroll {
          opacity: 0;
          transform: translateY(30px);
          animation-fill-mode: forwards;
        }
        .animate__fadeInUp {
          opacity: 1;
          transform: translateY(0);
        }
        .transition-all {
          transition: all 0.3s ease;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.6);
        }
        .btn:hover {
          transform: scale(1.05);
          box-shadow: 0 8px 30px rgba(212, 175, 55, 0.6);
        }
      `}</style>
    </div>
  );
};

export default TermsOfService;